/* eslint-disable @typescript-eslint/no-explicit-any */
import {store} from 'app/store'
import axios, {
  AxiosResponse,
  AxiosRequestConfig,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios'
import {userActions} from 'features/User'
import Logger from 'services/Logger'
import {HttpMethods, EndPoints} from './HTTP.types'
import {MissingAuthTokensError} from './errors'
import {isJWTTokenExpired} from 'utils/helpers'

const appendAPIVersion = (url: string): string => {
  const apiVersion = process.env.REACT_APP_API_BASE_URL_VERSION as string
  if (apiVersion) {
    return `${url}/${apiVersion}`
  }
  return url
}

export function getBaseUrl(): string {
  return appendAPIVersion(process.env.REACT_APP_API_BASE_URL as string)
}

export const getAnalyticsBaseUrl = (): string => {
  return process.env.REACT_APP_ANALYTICS_API_BASE_URL as string
}

export const getAuditBaseUrl = (): string => {
  return process.env.REACT_APP_AUDIT_API_BASE_URL as string
}

export const getViolationsBaseUrl = (): string => {
  return process.env.REACT_APP_VIOLATIONS_API_BASE_URL as string
}

export const getPBFFServiceBaseUrl = (): string => {
  return process.env.REACT_APP_ANALYTICS_ENDPOINTS_API_BASE_URL as string
}

export const getSIEMBaseUrl = (): string => {
  return process.env.REACT_APP_SIEM_API_BASE_URL as string
}

export const getAuthBaseUrl = (): string => {
  return process.env.REACT_APP_AUTH_API_BASE_URL as string
}

export const getEndpointBaseUrl = (): string => {
  return process.env.REACT_APP_ENDPOINTS_API_BASE_URL as string
}

export const getShadowBaseUrl = (): string => {
  return process.env.REACT_APP_SHADOW_FINDER_API_BASE_URL as string
}

export const apiKeysBaseUrl = (): string => {
  return process.env.REACT_APP_CREDS_BASE_URL as string
}

export const getEntitlementsBaseUrl = (): string => {
  return process.env.REACT_APP_ENTITLEMENTS_API_BASE_URL as string
}

export const getDopamineAssistantBaseUrl = (): string => {
  return (process.env.REACT_APP_DOPAMINE_ASSISTANT_API_BASE_URL ?? '').trim()
}

export const getFlightdeckBaseUrl = (): string => {
  return `${process.env.REACT_APP_FLIGHTDECK_API_BASE_URL as string}/v1`
}

const apiBaseUrl = getBaseUrl()

interface AxiosConfig extends InternalAxiosRequestConfig {
  retry?: boolean
}

interface AxiosErrorCustom extends AxiosError {
  config: AxiosConfig
}

const throwIfRequestCanceled = (error: AxiosErrorCustom): void => {
  if (axios.isCancel(error)) throw error
}

export const instance = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Flag to check if refresh token request is in progress.
 */
let isRefreshingToken = false

/**
 * Queue to store failed requests, which will be cleared after token refresh.
 */
let failedRequestsQueue: Array<{
  resolve: (token: string) => void
  reject: (error: any) => void
}> = []

/**
 * Function to process the failed requests queue.
 *
 * @param error - The error object to reject the promises with.
 * @param token - The token to resolve the promises with. Defaults to null.
 */
const processFailedRequestsQueue = (error: any, token: string | null): void => {
  failedRequestsQueue.forEach(promise => {
    if (error) {
      promise.reject(error)
    } else if (token !== null) {
      promise.resolve(token)
    } else {
      promise.reject(new Error('Token is null'))
    }
  })

  failedRequestsQueue = []
}

const isRetryableAuthError = (err: AxiosErrorCustom): boolean => {
  const status = err.response?.status
  return (
    err.config?.url !== EndPoints.Login &&
    (status === 401 || status === 403) &&
    !err.config?.retry
  )
}

const retryWithRefreshedToken = async (
  originalConfig: AxiosConfig,
): Promise<AxiosResponse> => {
  if (isRefreshingToken) {
    const token = await new Promise<string>((resolve, reject) => {
      failedRequestsQueue.push({resolve, reject})
    })
    originalConfig.headers['Authorization'] = `Bearer ${token}`
    return instance(originalConfig)
  }

  const {
    user: {tokens},
  } = store.getState()

  if (!tokens?.accessToken || !tokens?.refreshToken) {
    store.dispatch({type: 'reset/store'})
    throw new MissingAuthTokensError()
  }

  originalConfig.retry = true
  isRefreshingToken = true

  try {
    const rs = await instance.post(EndPoints.RefreshToken, {
      refreshToken: tokens.refreshToken,
    })
    const {accessToken} = rs.data
    store.dispatch(userActions.refreshTokens(accessToken))

    // When refresh token succeeds we retry the failed requests.
    processFailedRequestsQueue(null, accessToken)

    originalConfig.headers['Authorization'] = `Bearer ${accessToken}`
    return instance(originalConfig)
  } catch (error) {
    processFailedRequestsQueue(error, null)
    store.dispatch({type: 'reset/store'})
    throw error
  } finally {
    isRefreshingToken = false
  }
}

// Axios interceptor to add Authorization header to all requests.
instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const tokens = store.getState()?.user?.tokens

    if (
      process.env.REACT_APP_ENV !== 'test' &&
      tokens?.accessToken &&
      tokens?.refreshToken
    ) {
      if (
        isJWTTokenExpired(tokens.accessToken) &&
        isJWTTokenExpired(tokens.refreshToken)
      ) {
        store.dispatch({type: 'reset/store'})
      }
    }

    if (tokens?.accessToken) {
      config.headers['Authorization'] = `Bearer ${tokens?.accessToken}`
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  },
)

// Axios interceptor to handle token expiration and retry.
instance.interceptors.response.use(
  res => res,
  async (err: AxiosErrorCustom) => {
    throwIfRequestCanceled(err)

    if (err.response && isRetryableAuthError(err)) {
      return retryWithRefreshedToken(err.config)
    }

    if (err?.message?.includes('timeout of')) {
      err.message = 'Request timeout'
    }

    Logger.LogAPIError(err)

    return Promise.reject(err)
  },
)

export function requestHandler<T, D = Record<string, unknown>>({
  method,
  url,
  data,
  options = {},
}: {
  method: HttpMethods
  url: EndPoints | string
  data?: D
  options?: AxiosRequestConfig
}): Promise<AxiosResponse<T>> {
  const settings = options
  if (method === HttpMethods.DELETE) {
    settings['data'] = data
  }

  const payload = data ? data : {}

  switch (method) {
    case HttpMethods.POST:
      return instance.post<T>(url, payload, settings)
    case HttpMethods.PUT:
      return instance.put<T>(url, payload, settings)
    case HttpMethods.DELETE:
      return instance.delete<T>(url, settings)
    case HttpMethods.GET:
    default:
      return instance.get<T>(url, settings)
  }
}
